import { resolveAssetUrl } from "./app-base-url";
import { SAMPLE_MANIFEST, sampleUrl } from "./sample-manifest";
import type { AudioEngine, InstrumentId, SoundKey } from "./types";

const DEFAULT_VELOCITY = 0.9;
/** Short fade-out when cutting voices to avoid audible clicks. */
const ALL_NOTES_OFF_FADE_S = 0.05;

interface ActiveVoice {
  source: AudioBufferSourceNode;
  gain: GainNode;
}

/**
 * Production backend for the browser (and comparison reference in the
 * Android go/no-go): interactive AudioContext + preloaded AudioBuffers,
 * one BufferSource per trigger (unlimited polyphony de facto).
 */
export class WebAudioEngine implements AudioEngine {
  readonly backend = "web-audio" as const;

  private ctx: AudioContext | null = null;
  private readonly buffers = new Map<string, AudioBuffer>();
  private readonly loaded = new Set<InstrumentId>();
  private readonly voices = new Set<ActiveVoice>();

  async init(): Promise<void> {
    if (this.ctx) {
      return;
    }
    // Without Web Audio (jsdom in tests, fossil browsers) the engine stays
    // inert: playNote is a no-op instead of a crash.
    if (typeof AudioContext === "undefined") {
      return;
    }
    this.ctx = new AudioContext({ latencyHint: "interactive" });
  }

  unlock(): void {
    if (this.ctx?.state === "suspended") {
      void this.ctx.resume();
    }
  }

  async loadInstrument(instrument: InstrumentId): Promise<void> {
    if (this.loaded.has(instrument)) {
      return;
    }
    if (!this.ctx) {
      await this.init();
    }
    const { ctx } = this;
    if (!ctx) {
      throw new Error("AudioContext unavailable");
    }
    await Promise.all(
      SAMPLE_MANIFEST[instrument].map(async (key) => {
        const response = await fetch(resolveAssetUrl(sampleUrl(instrument, key)));
        if (!response.ok) {
          throw new Error(`Sample HTTP ${response.status}: ${sampleUrl(instrument, key)}`);
        }
        const bytes = await response.arrayBuffer();
        const buffer = await ctx.decodeAudioData(bytes);
        this.buffers.set(voiceId(instrument, key), buffer);
      }),
    );
    this.loaded.add(instrument);
  }

  isLoaded(instrument: InstrumentId): boolean {
    return this.loaded.has(instrument);
  }

  playNote(instrument: InstrumentId, key: SoundKey, velocity = DEFAULT_VELOCITY): void {
    const { ctx } = this;
    const buffer = this.buffers.get(voiceId(instrument, key));
    if (!ctx || !buffer) {
      return;
    }

    const gain = ctx.createGain();
    gain.gain.value = clamp01(velocity);
    gain.connect(ctx.destination);

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(gain);

    const voice: ActiveVoice = { source, gain };
    this.voices.add(voice);
    source.addEventListener("ended", () => {
      this.voices.delete(voice);
      gain.disconnect();
    });
    source.start();
  }

  allNotesOff(): void {
    const { ctx } = this;
    if (!ctx) {
      return;
    }
    for (const voice of this.voices) {
      voice.gain.gain.setTargetAtTime(0, ctx.currentTime, ALL_NOTES_OFF_FADE_S);
      voice.source.stop(ctx.currentTime + ALL_NOTES_OFF_FADE_S * 4);
    }
    this.voices.clear();
  }

  dispose(): void {
    this.allNotesOff();
    this.buffers.clear();
    this.loaded.clear();
    void this.ctx?.close();
    this.ctx = null;
  }
}

function voiceId(instrument: InstrumentId, key: SoundKey): string {
  return `${instrument}/${key}`;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}
